import { useEffect, useMemo, useState, type FormEvent } from "react";
import { ArrowLeft } from "lucide-react";
import {
  Button,
  EmptyState,
  FormField,
  Input,
  Select,
  Skeleton,
  Textarea,
  toast,
} from "@playbookai/ui";
import { useDefinitionNames } from "../hooks/useDefinitionNames.js";
import { useKpi } from "../hooks/useKpi.js";
import { useKpiCategoryOptions } from "../hooks/useKpiCategoryOptions.js";
import { useKpiDefinitions } from "../hooks/useKpiDefinitions.js";
import { useUpdateKpi } from "../hooks/useUpdateKpi.js";
import { useKpiFieldErrors } from "../hooks/useKpiFieldErrors.js";
import {
  KPI_REPORTING_FREQUENCIES,
  type ManagedKpiReportingFrequency,
  type UpdateKpiInput,
} from "../api/types.js";
import { ClearableCombobox } from "./ClearableCombobox.js";
import { FormSection } from "./FormSection.js";
import {
  DIRECTION_LABEL,
  REPORTING_FREQUENCY_LABEL,
} from "./kpi-presentation.js";
import { errorMessage } from "./error-message.js";

export interface EditKpiPageProps {
  readonly kpiId: string;
  /** Leave the form. The feature owns no router (ADR-0009) — the host
   * navigates, so Cancel and a successful save can land in different places. */
  readonly onCancel: () => void;
  /** The not-found state's "Back to All KPIs". Without it, it cancels. */
  readonly onBackToAll?: () => void;
  readonly onSaved: () => void;
  /** Reported whenever the edited-vs-pristine state flips, so the host can ask
   * before unsaved edits are discarded — the same seam Create uses. */
  readonly onDirtyChange?: (dirty: boolean) => void;
}

const FREQUENCY_OPTIONS = KPI_REPORTING_FREQUENCIES.map((frequency) => ({
  value: frequency,
  label: REPORTING_FREQUENCY_LABEL[frequency],
}));

/** The four editable fields as the KPI stated them when the form was filled,
 * plus the id they came from so a route change to another KPI refills. Nullable
 * columns are held as `""` here, matching what the controls hold. */
interface ManagedKpiFormValues {
  readonly kpiId: string;
  readonly name: string;
  readonly description: string;
  readonly categoryCode: string;
  readonly reportingFrequency: string;
}

/**
 * Edit a configured KPI's descriptive metadata (`PATCH /v1/kpis/:id`), as a
 * dedicated page under `/settings/kpi/:kpiId/edit`.
 *
 * A PAGE, NOT A MODAL, for the same reason Create is one: it is the same four
 * fields in the same order, and a form that appears as a page when you create a
 * KPI and as a dialog when you change it teaches two layouts for one task.
 *
 * FOUR FIELDS, AND THE OMISSIONS ARE THE DESIGN. `unit`, `direction` and the KPI
 * Type are shown READ-ONLY rather than hidden — a reader needs to know what the
 * measure is while renaming it, and needs to see that those facts are not the
 * ones on offer. Changing any of them would re-interpret every reading already
 * in the ledger, which is a product decision nobody has taken, and the server
 * refuses them regardless. Owner and target keep their own governance actions on
 * the KPI page; observations are never editable at all (ADR-0052).
 *
 * IT SENDS ONLY WHAT CHANGED. The request is a strict PATCH, so an untouched
 * field is omitted rather than echoed back — two admins editing different fields
 * of the same KPI cannot then clobber each other with stale form state, and the
 * audit row names only what actually moved. A form with nothing changed cannot
 * be submitted at all, which is also what the server's empty-patch 400 says.
 */
export function EditKpiPage({
  kpiId,
  onCancel,
  onBackToAll,
  onSaved,
  onDirtyChange,
}: EditKpiPageProps): JSX.Element {
  const query = useKpi(kpiId);
  const categoriesQuery = useKpiCategoryOptions();
  // The KPI Type is shown read-only on Edit (PB-5134, t17): a reader renaming
  // a measure needs to know which template it came from.
  const definitionName = useDefinitionNames([query.data?.definitionId ?? null]);
  const definitions = useKpiDefinitions();
  const templateDefinition = query.data?.definitionId
    ? definitions.data?.find((d) => d.definitionId === query.data?.definitionId)
    : undefined;
  const templateProvenance = templateDefinition
    ? templateDefinition.provenance === "system"
      ? "System"
      : "Custom"
    : null;
  const update = useUpdateKpi();

  const kpi = query.data;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [categoryCode, setCategoryCode] = useState("");
  const [reportingFrequency, setReportingFrequency] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const validation = useKpiFieldErrors(kpiId, {
    name,
    description,
    categoryCode,
    reportingFrequency,
  });
  // What the KPI said when the form was FILLED — a snapshot, not a live read.
  // This is the baseline every field is compared against, so "changed" means
  // changed from the values the user was actually shown.
  //
  // It has to be a snapshot because `query.data` keeps moving underneath it.
  // Comparing against the live row made a background refetch — or another
  // admin's concurrent rename — rewrite the meaning of the form without
  // touching it: an untouched Name suddenly read as CHANGED (it no longer
  // matched the server), `dirty` flipped true on its own, and Save would then
  // have sent the value the user was shown, silently reverting the other edit.
  // The mirror case is worse: type a name that happens to match what the server
  // has since been set to, and the field reads unchanged and is dropped from
  // the PATCH.
  const [prefill, setPrefill] = useState<ManagedKpiFormValues | null>(null);

  // Prefilled ONCE per KPI, when the detail lands. Keyed on the id rather than a
  // bare boolean so navigating from one KPI's edit page to another's refills;
  // re-running on every `query.data` identity change would overwrite what the
  // user typed each time a background refetch settled.
  useEffect(() => {
    if (!kpi || prefill?.kpiId === kpi.kpiId) return;
    const loaded: ManagedKpiFormValues = {
      kpiId: kpi.kpiId,
      name: kpi.name,
      description: kpi.description,
      categoryCode: kpi.categoryCode ?? "",
      reportingFrequency: kpi.reportingFrequency ?? "",
    };
    setName(loaded.name);
    setDescription(loaded.description);
    setCategoryCode(loaded.categoryCode);
    setReportingFrequency(loaded.reportingFrequency);
    setNameTouched(false);
    setPrefill(loaded);
  }, [kpi, prefill]);

  const categoryOptions = useMemo(() => {
    const options = (categoriesQuery.data ?? []).map((option) => ({
      value: option.code,
      // A code the installed catalog no longer describes still has to be
      // pickable; showing the code is truthful, showing nothing is not.
      label: option.name ?? option.code,
    }));
    // The KPI's own code may belong to a pack this list does not know, or the
    // options may not have loaded yet — without this the Combobox would render
    // an empty box over a real selection and a save would silently clear it.
    if (categoryCode && !options.some((o) => o.value === categoryCode)) {
      options.push({ value: categoryCode, label: categoryCode });
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [categoriesQuery.data, categoryCode]);

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();

  // Compared against the PREFILL SNAPSHOT, field by field, because that is also
  // how the request is built — anything reported as changed here is exactly what
  // gets sent, and it is exactly what the user changed.
  const changed = prefill
    ? {
        name: trimmedName !== prefill.name,
        description: trimmedDescription !== prefill.description,
        categoryCode: categoryCode !== prefill.categoryCode,
        reportingFrequency: reportingFrequency !== prefill.reportingFrequency,
      }
    : null;
  const dirty = changed ? Object.values(changed).some(Boolean) : false;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // Refresh / close / an off-SPA link: the only exits the host's own prompt
  // cannot reach, so hand them to the browser's native guard — same as Create.
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const nameError =
    nameTouched && !trimmedName ? "Name is required." : undefined;
  const canSubmit =
    Boolean(trimmedName) &&
    dirty &&
    prefill?.kpiId === kpiId &&
    !update.isPending;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setNameTouched(true);
    if (!changed || !canSubmit) return;

    const input: { -readonly [K in keyof UpdateKpiInput]?: UpdateKpiInput[K] } =
      {};
    if (changed.name) input.name = trimmedName;
    if (changed.description) input.description = trimmedDescription;
    // Empty string is how these two are CLEARED on screen; `null` is how a
    // clear is stated on the wire, because the columns are nullable and an
    // omitted key means "unchanged" rather than "empty".
    if (changed.categoryCode) input.categoryCode = categoryCode || null;
    if (changed.reportingFrequency) {
      input.reportingFrequency =
        (reportingFrequency as ManagedKpiReportingFrequency) || null;
    }

    const request = validation.beginRequest();
    update.mutate(
      { kpiId, input },
      {
        onSuccess: () => {
          if (!request.isCurrent()) return;
          toast("KPI updated", { tone: "healthy" });
          onSaved();
        },
        onError: (err) => {
          if (!request.reject(err)) return;
          toast(errorMessage(err, "Could not update the KPI."), {
            tone: "critical",
          });
        },
      },
    );
  };

  const templateLabel = kpi?.definitionId
    ? `${definitionName(kpi.definitionId)}${templateProvenance ? ` (${templateProvenance})` : ""}`
    : "No template";

  const backLink = (
    <Button
      variant="ghost"
      size="sm"
      onClick={onCancel}
      disabled={update.isPending}
      className="-ms-2 mb-3.5"
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
      {kpi?.name ?? "Back to KPI"}
    </Button>
  );

  if (query.isLoading) {
    return (
      <div className="space-y-5" aria-busy="true">
        {backLink}
        <Skeleton className="h-8 w-1/2" />
        {/* AP-24: rounded-lg override → shape="panel" (no picture change). */}
        <Skeleton className="h-64 w-full" shape="panel" />
      </div>
    );
  }

  if (query.isError || !kpi) {
    return (
      <div className="space-y-5">
        {backLink}
        <EmptyState
          variant="error"
          title="KPI not found"
          description="This KPI could not be loaded. It may have been removed, or the link may point at a KPI in another workspace."
          action={
            <Button onClick={onBackToAll ?? onCancel}>Back to All KPIs</Button>
          }
        />
      </div>
    );
  }

  return (
    <section
      aria-labelledby="edit-kpi-heading"
      className="mx-auto w-full max-w-162"
    >
      {backLink}
      {/* DS keep: SectionHeader fixes its description at max-w-xl + leading-relaxed and its root at mb-6, so it wraps this intro and moves the form down. */}
      <div className="mb-4 space-y-1">
        <h1
          id="edit-kpi-heading"
          className="text-heading-3 tracking-tight text-pb-text"
        >
          Edit KPI
        </h1>
        <p className="text-body text-pb-text-secondary">
          Update the descriptive details. Ownership and targets are changed from
          the KPI’s page.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3.5">
        <FormSection title="KPI details">
          <FormField
            label="Name"
            required
            error={nameError ?? validation.errors.name}
          >
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              onBlur={() => setNameTouched(true)}
              maxLength={255}
              placeholder="e.g. Platform Team Throughput"
            />
          </FormField>
          <FormField label="Description" error={validation.errors.description}>
            <Textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
              maxLength={500}
              showCount
            />
          </FormField>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Category" error={validation.errors.categoryCode}>
              <ClearableCombobox
                options={categoryOptions}
                value={categoryCode}
                onChange={setCategoryCode}
                placeholder="Search categories…"
                emptyMessage="No categories found"
                clearLabel="Clear category"
              />
            </FormField>
            <FormField
              label="Reporting frequency"
              error={validation.errors.reportingFrequency}
            >
              <Select
                options={FREQUENCY_OPTIONS}
                value={reportingFrequency}
                onChange={setReportingFrequency}
                placeholder="Select a frequency"
              />
            </FormField>
          </div>
        </FormSection>

        <FormSection
          title="Measurement"
          description="Locked once a KPI exists — changing it would re-interpret every value already recorded."
        >
          {/* Shown, not hidden, and not editable: a reader renaming a measure
              needs to know what it measures, and needs to see that these are
              deliberately not on offer rather than merely missing. */}
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Unit">
              <Input value={kpi.unit} readOnly variant="readonly" />
            </FormField>
            <FormField label="Direction">
              <Input
                value={DIRECTION_LABEL[kpi.direction]}
                readOnly
                variant="readonly"
              />
            </FormField>
          </div>
          <FormField label="KPI Template">
            <Input value={templateLabel} readOnly variant="readonly" />
          </FormField>
        </FormSection>

        {/* Kept hand-built (not SettingsSaveBar): that part always shows a
            saved/unsaved hint line, new content this footer has never had,
            and its "Reset to defaults" semantics don't match Cancel-and-
            leave — not an approved T01 look row. */}
        <div className="mt-5.5 flex flex-wrap items-center justify-end gap-3 border-t border-pb-border/60 pt-4.5">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={update.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit}
            loading={update.isPending}
          >
            Save changes
          </Button>
        </div>
      </form>
    </section>
  );
}
